import * as React from "react";
import { Check, ChevronDown } from "lucide-react";

import { Badge } from "../badge";
import {
  SearchSelectClear,
  SearchSelectCommand,
  SearchSelectContent,
  SearchSelectEmpty,
  SearchSelectGroup,
  SearchSelectInput,
  SearchSelectItem,
  SearchSelectList,
  SearchSelectRoot,
  SearchSelectTrigger,
} from "../search-select-v2";
import { cn } from "../../lib/utils";

export type MultiSelectOption<T = unknown> = {
  value: string;
  label: string;
  disabled?: boolean;
  data?: T;
};

export type ItemSelectCustomProps<T = unknown> = {
  option: MultiSelectOption<T>;
  isSelected: boolean;
  isDisabled: boolean;
};

type MultiSelectProps<T = unknown> = {
  value: string[];
  onChange: (values: string[]) => void;
  options: MultiSelectOption<T>[];
  placeholder?: string;
  ariaLabel?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  triggerClassName?: string;
  disabled?: boolean;
  maxBadges?: number;
  showClear?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onClear?: () => void;
  showExtraCount?: boolean;
  wrapBadges?: boolean;
  onExtraClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  sideOffset?: number;
};

export function MultiSelectV2<T = unknown>({
  value,
  onChange,
  options,
  placeholder = "Select...",
  ariaLabel,
  searchPlaceholder = "Search...",
  emptyMessage = "No results.",
  className,
  triggerClassName,
  disabled = false,
  maxBadges = 2,
  showClear = true,
  open,
  onOpenChange,
  onClear,
  showExtraCount = true,
  wrapBadges = true,
  onExtraClick,
  side = "bottom",
  align = "start",
  sideOffset = 8,
}: MultiSelectProps<T>) {
  const selected = options.filter((option) => value.includes(option.value));
  const extraCount = Math.max(selected.length - maxBadges, 0);

  const toggle = (optionValue: string) => {
    onChange(
      value.includes(optionValue)
        ? value.filter((currentValue) => currentValue !== optionValue)
        : [...value, optionValue],
    );
  };

  return (
    <div className={cn("w-full", className)}>
      <SearchSelectRoot
        open={open}
        onOpenChange={onOpenChange}
        disabled={disabled}
      >
        <div className="relative w-full">
          <SearchSelectTrigger
            aria-label={ariaLabel}
            className={cn(
              "relative flex min-h-10 w-full items-center justify-start gap-1 rounded-md border-gray-200 bg-white px-3 py-2 pr-10 text-left hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700",
              !wrapBadges && "flex-nowrap overflow-hidden",
              triggerClassName,
            )}
          >
            <TriggerContent />
          </SearchSelectTrigger>
          {showClear && selected.length > 0 && (
            <SearchSelectClear
              className="right-2 p-1"
              onClear={() => onClear?.()}
            />
          )}
        </div>

        <SearchSelectContent
          className="z-[1401] w-[--radix-popover-trigger-width] p-0 contain-layout bg-white dark:bg-gray-800"
          side={side}
          align={align}
          sideOffset={sideOffset}
        >
          <SearchSelectCommand>
            <SearchSelectInput
              placeholder={searchPlaceholder}
              className="py-2"
              aria-label={searchPlaceholder}
            />
            <SearchSelectList>
              <SearchSelectEmpty>{emptyMessage}</SearchSelectEmpty>
              <SearchSelectGroup>
                {options.map((option) => {
                  const isSelected = value.includes(option.value);
                  const isDisabled = option.disabled ?? false;
                  return (
                    <SearchSelectItem
                      key={option.value}
                      value={`${option.value} ${option.label}`}
                      disabled={isDisabled}
                      onSelect={() => {
                        if (!isDisabled) toggle(option.value);
                      }}
                      className={cn(
                        "flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-gray-900 hover:bg-gray-100 aria-selected:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-700 dark:aria-selected:bg-gray-700",
                        isDisabled && "pointer-events-none opacity-50",
                      )}
                    >
                      <span
                        className={cn(
                          "flex h-5 w-5 items-center justify-center rounded border",
                          isSelected
                            ? "border-[#10B981] bg-[#10B981]"
                            : "border-[#DADADA] bg-white dark:border-gray-500 dark:bg-gray-700",
                        )}
                      >
                        {isSelected && (
                          <Check
                            className="h-3 w-3 text-white"
                            strokeWidth={3}
                          />
                        )}
                      </span>
                      <span className="truncate">{option.label}</span>
                    </SearchSelectItem>
                  );
                })}
              </SearchSelectGroup>
            </SearchSelectList>
          </SearchSelectCommand>
        </SearchSelectContent>
      </SearchSelectRoot>
    </div>
  );

  function TriggerContent() {
    if (selected.length === 0) {
      return <span className="truncate text-gray-500 dark:text-gray-400">{placeholder}</span>;
    }

    return (
      <div
        className={cn(
          "flex min-w-0 items-center gap-1",
          wrapBadges ? "flex-wrap" : "flex-nowrap overflow-hidden",
        )}
      >
        {selected.slice(0, showExtraCount ? maxBadges : selected.length).map((option) => (
          <Badge
            key={option.value}
            variant="secondary"
            className="shrink-0 whitespace-nowrap"
          >
            {option.label}
          </Badge>
        ))}
        {showExtraCount && extraCount > 0 && (
          <Badge
            variant="secondary"
            className={cn("shrink-0 whitespace-nowrap", onExtraClick && "cursor-pointer")}
            onPointerDown={(event) => {
              if (onExtraClick) event.stopPropagation();
            }}
            onClick={(event) => {
              if (!onExtraClick) return;
              event.stopPropagation();
              event.preventDefault();
              onExtraClick(event as unknown as React.MouseEvent<HTMLDivElement>);
            }}
          >
            +{extraCount}
          </Badge>
        )}
        <ChevronDown className="absolute right-3 h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" />
      </div>
    );
  }
}
